import {
  createQaThread,
  deleteQaThread,
  getQaThread,
  listQaThreads,
  sendQaMessage,
} from '../api.js';
import { showError, showSuccess } from '../utils/toast.js';

const html = String.raw;

const normalizeValue = (value) => String(value ?? '').trim();

const escapeHtml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const renderMultiline = (value) => escapeHtml(value).replace(/\n/g, '<br />');

const normalizeContext = (context = {}) => {
  const scope = context?.scope === 'conversation' ? 'conversation' : 'global';
  const normalized = { scope };
  const assign = (field) => {
    const value = normalizeValue(context?.[field]);
    if (value && value !== '__none__') {
      normalized[field] = value;
    }
  };

  if (scope === 'conversation') {
    assign('conversationId');
  }
  assign('customerId');
  assign('projectId');
  assign('from');
  assign('to');
  return normalized;
};

const contextSignature = (context) => JSON.stringify(normalizeContext(context));

const contextsEqual = (left, right) => contextSignature(left) === contextSignature(right);

const formatDateTime = (value) => {
  const normalized = normalizeValue(value);
  if (!normalized) return '';
  const time = Date.parse(normalized);
  if (!Number.isFinite(time)) return normalized;
  return new Date(time).toLocaleString();
};

const formatSeconds = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '';
  return `${numeric.toFixed(1)}s`;
};

const compareByUpdatedAt = (left, right) => Date.parse(right?.updatedAt ?? '') - Date.parse(left?.updatedAt ?? '');
const compareByCreatedAt = (left, right) => Date.parse(left?.createdAt ?? '') - Date.parse(right?.createdAt ?? '');

const mergeMessages = (current, incoming) => {
  const map = new Map();
  current.forEach((message) => {
    if (message?.messageId) map.set(message.messageId, message);
  });
  incoming.forEach((message) => {
    if (message?.messageId) map.set(message.messageId, message);
  });
  return Array.from(map.values()).sort(compareByCreatedAt);
};

const truncate = (value, max = 160) => {
  const normalized = normalizeValue(value);
  if (!normalized) return '';
  if (normalized.length <= max) return normalized;
  return `${normalized.slice(0, max - 1)}…`;
};

const defaultCopy = {
  title: 'Q&A-Chat mit Quellen',
  description: 'Stelle Fragen zu Anforderungen und Transkripten.',
  roles: {
    user: 'Du',
    assistant: 'Assistent',
  },
  state: {
    loading: 'Chat wird geladen…',
    empty: 'Noch keine Nachrichten. Stelle deine erste Frage.',
  },
  input: {
    placeholder: 'Beispiel: Welche Farbe soll das Haus sein?',
  },
  actions: {
    send: 'Senden',
    newThread: 'Neuer Thread',
    deleteThread: 'Löschen',
    includeGeneralAnswer: '+ Allgemeinwissen (Test)',
    searchGlobal: 'Global suchen',
  },
  labels: {
    generalAnswer: 'Allgemeinwissen (Test)',
  },
  feedback: {
    threadDeleted: 'Thread gelöscht.',
  },
  citations: {
    title: 'Quellen',
    unknownSpeaker: 'Unbekannter Sprecher',
    unknownCustomer: 'Unbekannter Kunde',
    unknownProject: 'Unbekanntes Projekt',
  },
  errors: {
    default: 'Anfrage fehlgeschlagen.',
    send: 'Nachricht konnte nicht gesendet werden.',
    loadThread: 'Thread konnte nicht geladen werden.',
    createThread: 'Thread konnte nicht erstellt werden.',
    deleteThread: 'Thread konnte nicht gelöscht werden.',
    loadThreads: 'Threads konnten nicht geladen werden.',
  },
};

export const createQaChatPanel = ({
  copy = defaultCopy,
  title = '',
  description = '',
  context: initialContext = { scope: 'global' },
} = {}) => {
  const resolvedCopy = {
    ...defaultCopy,
    ...copy,
    roles: { ...defaultCopy.roles, ...(copy?.roles ?? {}) },
    state: { ...defaultCopy.state, ...(copy?.state ?? {}) },
    input: { ...defaultCopy.input, ...(copy?.input ?? {}) },
    actions: { ...defaultCopy.actions, ...(copy?.actions ?? {}) },
    labels: { ...defaultCopy.labels, ...(copy?.labels ?? {}) },
    feedback: { ...defaultCopy.feedback, ...(copy?.feedback ?? {}) },
    citations: { ...defaultCopy.citations, ...(copy?.citations ?? {}) },
    errors: { ...defaultCopy.errors, ...(copy?.errors ?? {}) },
  };

  const state = {
    threads: [],
    activeThread: null,
    messages: [],
    draft: '',
    loadingThreads: false,
    loadingMessages: false,
    sending: false,
    deleting: false,
    includeGeneralAnswer: false,
    error: '',
  };

  let host = null;
  let initialized = false;
  let destroyed = false;
  let context = normalizeContext(initialContext);
  let contextSig = contextSignature(context);
  let threadRequestId = 0;
  let messageRequestId = 0;
  let lockedViewportTop = null;

  const sortThreads = (threads = []) => [...threads].sort(compareByUpdatedAt);
  const contextThreads = (threads = state.threads) =>
    sortThreads(threads.filter((thread) => contextsEqual(thread?.context, context)));
  const findThreadById = (threadId, threads = state.threads) =>
    threads.find((thread) => thread?.threadId === threadId) ?? null;
  const relatedQuestionForIndex = (messages = [], index = -1) => {
    for (let i = index - 1; i >= 0; i -= 1) {
      const item = messages[i];
      if (item?.role !== 'user') continue;
      const text = normalizeValue(item?.content);
      if (text) return text;
    }
    return '';
  };

  const renderCitations = (citations = []) => {
    if (!Array.isArray(citations) || !citations.length) return '';
    return html`
      <div class="qa-chat-citations stack tight">
        <span class="muted tiny">${escapeHtml(resolvedCopy.citations.title)}</span>
        <div class="qa-chat-citation-list">
          ${citations
            .map((citation) => {
              const speaker = normalizeValue(citation?.speaker) || resolvedCopy.citations.unknownSpeaker;
              const customer = normalizeValue(citation?.customerId) || resolvedCopy.citations.unknownCustomer;
              const project = normalizeValue(citation?.projectId) || resolvedCopy.citations.unknownProject;
              const start = formatSeconds(citation?.startSec);
              const end = formatSeconds(citation?.endSec);
              const timeRange = start && end ? `${start} – ${end}` : '';
              const conversationId = normalizeValue(citation?.conversationId);
              const conversationLabel = conversationId ? `#${conversationId.slice(0, 8)}` : '';
              const quote = truncate(citation?.quote, 140);
              const metaParts = [speaker, `${customer} / ${project}`, timeRange, conversationLabel].filter(Boolean);
              return html`
                <div class="qa-chat-citation">
                  <span>${escapeHtml(metaParts.join(' · '))}</span>
                  ${quote ? `<span class="muted tiny">${escapeHtml(quote)}</span>` : ''}
                </div>
              `;
            })
            .join('')}
        </div>
      </div>
    `;
  };

  const renderThreadTabs = () => {
    const scopedThreads = contextThreads();
    if (!scopedThreads.length) return '';

    return html`
      <div class="qa-chat-thread-tabs" role="tablist">
        ${scopedThreads
          .map((thread) => {
            const threadId = normalizeValue(thread?.threadId);
            if (!threadId) return '';
            const label = truncate(normalizeValue(thread?.title) || `#${threadId.slice(0, 8)}`, 46);
            const isActive = state.activeThread?.threadId === threadId;
            return html`
              <div class="qa-chat-thread-tab ${isActive ? 'is-active' : ''}">
                <button type="button" class="qa-chat-thread-tab-select" data-qa-tab="${escapeHtml(threadId)}">
                  ${escapeHtml(label)}
                </button>
                <button
                  type="button"
                  class="qa-chat-thread-tab-close"
                  data-qa-tab-close="${escapeHtml(threadId)}"
                  aria-label="${escapeHtml(resolvedCopy.actions.deleteThread)}"
                  ${state.deleting ? 'disabled' : ''}
                >
                  ×
                </button>
              </div>
            `;
          })
          .join('')}
      </div>
    `;
  };

  const renderMessages = () => {
    if (state.loadingThreads || state.loadingMessages) {
      return `<p class="qa-chat-loading muted">${escapeHtml(resolvedCopy.state.loading)}</p>`;
    }
    if (!state.messages.length) {
      return `<p class="qa-chat-empty muted">${escapeHtml(resolvedCopy.state.empty)}</p>`;
    }
    return state.messages
      .map((message, index) => {
        const role = message?.role === 'assistant' ? 'assistant' : 'user';
        const roleLabel = role === 'assistant' ? resolvedCopy.roles.assistant : resolvedCopy.roles.user;
        const createdAt = formatDateTime(message?.createdAt);
        const answerVariant = normalizeValue(message?.metadata?.answerVariant).toLowerCase();
        const isGeneralAnswer = answerVariant === 'general';
        const scopeSuggestion = normalizeValue(message?.metadata?.scopeSuggestion).toLowerCase();
        const relatedQuestion = role === 'assistant' ? relatedQuestionForIndex(state.messages, index) : '';
        return html`
          <article class="qa-chat-message ${role === 'assistant' ? 'is-assistant' : 'is-user'}">
            <div class="row qa-chat-message-meta">
              <div class="row" style="gap:0.35rem; align-items:center;">
                <strong>${escapeHtml(roleLabel)}</strong>
                ${isGeneralAnswer ? `<span class="qa-chat-variant-badge">${escapeHtml(resolvedCopy.labels.generalAnswer)}</span>` : ''}
              </div>
              ${createdAt ? `<span class="muted tiny">${escapeHtml(createdAt)}</span>` : ''}
            </div>
            <p class="qa-chat-message-text">${renderMultiline(message?.content || '')}</p>
            ${role === 'assistant' ? renderCitations(message?.citations) : ''}
            ${role === 'assistant' && scopeSuggestion === 'global' && relatedQuestion
              ? `
                <div class="row qa-chat-message-actions">
                  <button type="button" class="ghost-button small" data-qa-global-search="${escapeHtml(relatedQuestion)}">
                    ${escapeHtml(resolvedCopy.actions.searchGlobal)}
                  </button>
                </div>
              `
              : ''}
          </article>
        `;
      })
      .join('');
  };

  const lockViewport = () => {
    if (typeof window === 'undefined') return;
    const current = Number(window.scrollY);
    lockedViewportTop = Number.isFinite(current) ? current : null;
  };

  const restoreViewport = () => {
    if (lockedViewportTop === null || typeof window === 'undefined') return;
    window.scrollTo(0, lockedViewportTop);
    if (!state.sending) {
      lockedViewportTop = null;
    }
  };

  const render = ({ scrollToBottom = false } = {}) => {
    if (!host || destroyed) return;

    const headerTitle = normalizeValue(title) || resolvedCopy.title;
    const headerDescription = normalizeValue(description) || resolvedCopy.description;
    const activeThreadTitle = normalizeValue(state.activeThread?.title) || normalizeValue(state.activeThread?.threadId);
    const sendDisabled = state.sending || state.loadingThreads || state.loadingMessages || !normalizeValue(state.draft);

    host.innerHTML = html`
      <div class="card qa-chat-panel stack">
        <div class="row qa-chat-header">
          <div class="stack tight">
            <h3 class="qa-chat-title">${escapeHtml(headerTitle)}</h3>
            ${headerDescription ? `<p class="muted small">${escapeHtml(headerDescription)}</p>` : ''}
            ${activeThreadTitle ? `<span class="muted tiny">${escapeHtml(activeThreadTitle)}</span>` : ''}
          </div>
          <div class="row qa-chat-actions">
            <button type="button" class="ghost-button small" data-qa-new ${state.sending ? 'disabled' : ''}>
              ${escapeHtml(resolvedCopy.actions.newThread)}
            </button>
          </div>
        </div>

        ${renderThreadTabs()}

        <div class="qa-chat-messages" data-qa-messages>
          ${renderMessages()}
        </div>

        <form class="row qa-chat-form" data-qa-form>
          <input
            type="text"
            data-qa-input
            value="${escapeHtml(state.draft)}"
            placeholder="${escapeHtml(resolvedCopy.input.placeholder)}"
            ${state.sending ? 'disabled' : ''}
          />
          <button type="submit" class="button button-small" ${sendDisabled ? 'disabled' : ''}>
            ${escapeHtml(resolvedCopy.actions.send)}
          </button>
        </form>

        <label class="qa-chat-general-toggle">
          <input type="checkbox" data-qa-general-toggle ${state.includeGeneralAnswer ? 'checked' : ''} ${state.sending ? 'disabled' : ''} />
          <span>${escapeHtml(resolvedCopy.actions.includeGeneralAnswer)}</span>
        </label>

        ${state.error ? `<p class="qa-chat-error">${escapeHtml(state.error)}</p>` : ''}
      </div>
    `;

    const input = host.querySelector('[data-qa-input]');
    const form = host.querySelector('[data-qa-form]');
    const newButton = host.querySelector('[data-qa-new]');
    const generalToggle = host.querySelector('[data-qa-general-toggle]');
    const messagesEl = host.querySelector('[data-qa-messages]');
    const submitButton = form?.querySelector('button[type="submit"]');

    const updateSubmitState = () => {
      if (!submitButton) return;
      submitButton.disabled =
        state.sending ||
        state.loadingThreads ||
        state.loadingMessages ||
        !normalizeValue(state.draft);
    };

    input?.addEventListener('input', (event) => {
      state.draft = event.target?.value ?? '';
      if (state.error) {
        state.error = '';
        const errorEl = host.querySelector('.qa-chat-error');
        if (errorEl) {
          errorEl.remove();
        }
      }
      updateSubmitState();
    });

    form?.addEventListener('submit', (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (!state.sending && normalizeValue(state.draft)) {
        lockViewport();
      }
      void sendMessage();
    });

    newButton?.addEventListener('click', () => {
      void startNewThread();
    });

    generalToggle?.addEventListener('change', (event) => {
      state.includeGeneralAnswer = Boolean(event.target?.checked);
    });

    host.querySelectorAll('[data-qa-tab]').forEach((button) => {
      button.addEventListener('click', () => {
        const threadId = normalizeValue(button.getAttribute('data-qa-tab'));
        if (!threadId) return;
        void selectThread(threadId);
      });
    });

    host.querySelectorAll('[data-qa-tab-close]').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        const threadId = normalizeValue(button.getAttribute('data-qa-tab-close'));
        if (!threadId) return;
        void removeThreadById(threadId);
      });
    });

    host.querySelectorAll('[data-qa-global-search]').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        const question = normalizeValue(button.getAttribute('data-qa-global-search'));
        if (!question || state.sending) return;
        lockViewport();
        void sendMessage({ manualMessage: question, searchScope: 'global' });
      });
    });

    if (scrollToBottom && messagesEl) {
      messagesEl.scrollTop = messagesEl.scrollHeight;
    }
    restoreViewport();
  };

  const loadThreadById = async (threadId, options = {}) => {
    const requestId = ++messageRequestId;
    if (!options.silent) {
      state.loadingMessages = true;
      state.error = '';
      render();
    }

    try {
      const payload = await getQaThread(threadId, { messageLimit: 250 });
      if (requestId !== messageRequestId || destroyed) return null;
      state.activeThread = payload?.thread ?? null;
      state.messages = Array.isArray(payload?.messages) ? payload.messages.sort(compareByCreatedAt) : [];
      state.error = '';
      render({ scrollToBottom: true });
      return state.activeThread;
    } catch (error) {
      if (requestId !== messageRequestId || destroyed) return null;
      state.error = error?.message || resolvedCopy.errors.loadThread;
      if (!options.silent) {
        showError(state.error);
      }
      render();
      return null;
    } finally {
      if (requestId === messageRequestId && !destroyed) {
        state.loadingMessages = false;
        render();
      }
    }
  };

  const loadThreads = async (options = {}) => {
    const requestId = ++threadRequestId;
    state.loadingThreads = true;
    state.error = '';
    render();

    try {
      const payload = await listQaThreads({ limit: 50, contextSignature: contextSig });
      if (requestId !== threadRequestId || destroyed) return;
      const threads = Array.isArray(payload) ? payload : [];
      state.threads = sortThreads(threads);
      const scopedThreads = contextThreads(state.threads);
      if (!scopedThreads.length) {
        state.activeThread = null;
        state.messages = [];
        render();
        return;
      }

      const currentId = normalizeValue(state.activeThread?.threadId);
      const preferredThread = findThreadById(currentId, scopedThreads) ?? scopedThreads[0];
      if (preferredThread && normalizeValue(state.activeThread?.threadId) !== preferredThread.threadId) {
        await loadThreadById(preferredThread.threadId, { silent: true });
      } else {
        state.activeThread = preferredThread ?? null;
      }
      render();
    } catch (error) {
      if (requestId !== threadRequestId || destroyed) return;
      state.error = error?.message || resolvedCopy.errors.loadThreads;
      if (!options.silent) {
        showError(state.error);
      }
      render();
    } finally {
      if (requestId === threadRequestId && !destroyed) {
        state.loadingThreads = false;
        render();
      }
    }
  };

  const ensureThread = async () => {
    if (state.activeThread && contextsEqual(state.activeThread.context, context)) {
      return state.activeThread;
    }

    const matching = contextThreads(state.threads)[0] ?? null;
    if (matching?.threadId) {
      await loadThreadById(matching.threadId, { silent: true });
      return state.activeThread;
    }

    const response = await createQaThread({ context });
    const thread = response?.thread;
    if (!thread?.threadId) {
      throw new Error(resolvedCopy.errors.createThread);
    }
    state.threads = sortThreads([thread, ...state.threads.filter((item) => item.threadId !== thread.threadId)]);
    state.activeThread = thread;
    state.messages = [];
    render();
    return thread;
  };

  const sendMessage = async ({ manualMessage = '', searchScope = 'context' } = {}) => {
    const usingManualMessage = Boolean(normalizeValue(manualMessage));
    const message = normalizeValue(usingManualMessage ? manualMessage : state.draft);
    const normalizedScope = normalizeValue(searchScope).toLowerCase() === 'global' ? 'global' : 'context';
    if (!message || state.sending || destroyed) {
      if (!state.sending) {
        lockedViewportTop = null;
      }
      return;
    }

    state.sending = true;
    state.error = '';
    render();

    try {
      const thread = await ensureThread();
      if (!thread?.threadId) {
        throw new Error(resolvedCopy.errors.createThread);
      }
      const response = await sendQaMessage(thread.threadId, {
        message,
        includeGeneralAnswer: state.includeGeneralAnswer,
        searchScope: normalizedScope,
      });
      const incoming = [response?.userMessage, response?.assistantMessage, response?.assistantGeneralMessage].filter(Boolean);
      state.messages = mergeMessages(state.messages, incoming);
      if (!usingManualMessage) {
        state.draft = '';
      }
      state.error = '';
      const timestamp =
        normalizeValue(response?.assistantGeneralMessage?.createdAt) ||
        normalizeValue(response?.assistantMessage?.createdAt) ||
        normalizeValue(response?.userMessage?.createdAt) ||
        new Date().toISOString();
      state.threads = sortThreads(
        state.threads.map((item) =>
          item.threadId === thread.threadId
            ? {
                ...item,
                updatedAt: timestamp,
              }
            : item,
        ),
      );
      state.activeThread = findThreadById(thread.threadId) ?? state.activeThread;
      render({ scrollToBottom: true });
    } catch (error) {
      state.error = error?.message || resolvedCopy.errors.send;
      showError(state.error);
      render();
    } finally {
      state.sending = false;
      render();
    }
  };

  const startNewThread = async () => {
    if (state.sending || destroyed) return;
    try {
      state.error = '';
      render();
      const response = await createQaThread({ context });
      const thread = response?.thread;
      if (!thread?.threadId) {
        throw new Error(resolvedCopy.errors.createThread);
      }
      state.threads = sortThreads([thread, ...state.threads.filter((item) => item.threadId !== thread.threadId)]);
      state.activeThread = thread;
      state.messages = [];
      state.draft = '';
      render();
    } catch (error) {
      state.error = error?.message || resolvedCopy.errors.createThread;
      showError(state.error);
      render();
    }
  };

  const selectThread = async (threadId) => {
    const normalized = normalizeValue(threadId);
    if (!normalized || state.loadingMessages || state.sending || destroyed) return;
    if (normalizeValue(state.activeThread?.threadId) === normalized) return;
    await loadThreadById(normalized, { silent: false });
  };

  const removeThreadById = async (threadId) => {
    const normalizedThreadId = normalizeValue(threadId);
    if (!normalizedThreadId || state.deleting || destroyed) return;
    const scopedThreads = contextThreads(state.threads);
    const targetIndex = scopedThreads.findIndex((thread) => thread.threadId === normalizedThreadId);
    if (targetIndex === -1) return;

    const isActive = normalizeValue(state.activeThread?.threadId) === normalizedThreadId;
    const replacementThread = isActive
      ? scopedThreads[targetIndex + 1] ?? scopedThreads[targetIndex - 1] ?? null
      : null;

    state.deleting = true;
    state.error = '';
    render();

    try {
      await deleteQaThread(normalizedThreadId);
      state.threads = state.threads.filter((thread) => thread.threadId !== normalizedThreadId);

      if (isActive && replacementThread?.threadId) {
        state.activeThread = null;
        state.messages = [];
        await loadThreadById(replacementThread.threadId, { silent: true });
      } else if (isActive) {
        state.activeThread = null;
        state.messages = [];
      }

      showSuccess(resolvedCopy.feedback.threadDeleted);
      render();
    } catch (error) {
      state.error = error?.message || resolvedCopy.errors.deleteThread;
      showError(state.error);
      render();
    } finally {
      state.deleting = false;
      render();
    }
  };

  const setHost = (nextHost) => {
    host = nextHost ?? null;
    if (!host || destroyed) return;
    render();
    if (!initialized) {
      initialized = true;
      void loadThreads({ silent: true });
    }
  };

  const setContext = (nextContext) => {
    const normalized = normalizeContext(nextContext);
    const nextSignature = contextSignature(normalized);
    if (nextSignature === contextSig) return;
    context = normalized;
    contextSig = nextSignature;
    state.error = '';
    state.activeThread = null;
    state.messages = [];
    if (initialized) {
      void loadThreads({ silent: true });
    }
    render();
  };

  const reload = () => {
    if (!initialized || destroyed) return;
    void loadThreads({ silent: false });
  };

  const destroy = () => {
    destroyed = true;
    threadRequestId += 1;
    messageRequestId += 1;
    host = null;
  };

  return {
    setHost,
    setContext,
    reload,
    destroy,
  };
};
